import React from "react";
import { Box, Button, Card, Flex, Progress, Text } from "@radix-ui/themes";
import {
  Area,
  AreaChart,
  CartesianGrid,
  Funnel,
  FunnelChart,
  LabelList,
  ResponsiveContainer,
  Tooltip as ChartTooltip,
  XAxis,
  YAxis,
} from "recharts";
import { ToastAlert } from "components";
import { useProfileContext } from "context";
import { useRefreshWhatsappMessagingLimits, useWhatsappAnalytics } from "hooks";
import { useToast } from "../../context/ToastContext";
import WhatsappMessagingLimitHelp from "./WhatsappMessagingLimitHelp";

const EVENT_COLORS = {
  sent: "#22c55e",
  delivered: "#14b8a6",
  read: "#3b82f6",
  failed: "#ef4444",
  undelivered: "#f97316",
  canceled: "#6b7280",
};

const FUNNEL_COLORS = ["#16a34a", "#14b8a6", "#3b82f6"];

function formatNumber(value) {
  return Number(value || 0).toLocaleString();
}

function formatPercent(value) {
  return `${Number(value || 0).toFixed(Number(value || 0) % 1 === 0 ? 0 : 1)}%`;
}

function formatDateTime(value) {
  if (!value) return "Never refreshed";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Never refreshed";
  return date.toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function MetricCard({ label, value, detail }) {
  return (
    <Card style={{ flex: "1 1 170px", minWidth: 170, padding: "var(--space-3)" }}>
      <Flex direction="column" gap="3">
        <Text size="2" color="gray" weight="medium">{label}</Text>
        <Flex direction="column" gap="1">
          <Text size="6" weight="bold">{value}</Text>
          <Text size="1" color="gray">{detail}</Text>
        </Flex>
      </Flex>
    </Card>
  );
}

function SectionCard({ title, subtitle, children, style }) {
  return (
    <Card style={{ padding: "var(--space-3)", ...style }}>
      <Flex direction="column" gap="3" style={{ height: "100%" }}>
        <Flex direction="column" gap="1">
          <Text size="3" weight="medium">{title}</Text>
          {subtitle ? <Text size="2" color="gray">{subtitle}</Text> : null}
        </Flex>
        {children}
      </Flex>
    </Card>
  );
}

function EmptyChart({ label }) {
  return (
    <Flex align="center" justify="center" style={{ height: "100%", minHeight: 150, border: "1px dashed var(--gray-a6)", borderRadius: "var(--radius-3)" }}>
      <Text size="2" color="gray">{label}</Text>
    </Flex>
  );
}

function MessagingLimitCard({ limits, onRefresh, isRefreshing }) {
  const totalLimit = limits?.total_limit;
  const totalUsed = Number(limits?.total_used || 0);
  const totalRemaining = limits?.total_remaining;
  const accounts = limits?.accounts || [];
  const hasKnownLimit = totalLimit !== null && typeof totalLimit !== "undefined";
  const usedPercent = hasKnownLimit && totalLimit > 0
    ? Math.min(100, Math.round((totalUsed / totalLimit) * 100))
    : 0;

  return (
    <SectionCard
      title="Messaging Limit"
      subtitle="Local rolling 24-hour unique-recipient capacity"
      style={{ alignSelf: "stretch" }}
    >
      <Flex direction="column" gap="3">
        <Flex justify="between" align="start" gap="3" wrap="wrap">
          <Flex direction="column" gap="1">
            <Text size="6" weight="bold">
              {hasKnownLimit
                ? `${formatNumber(totalRemaining)} / ${formatNumber(totalLimit)}`
                : "Refresh required"}
            </Text>
            <Text size="2" color="gray">
              {hasKnownLimit
                ? `${formatNumber(totalUsed)} used in the last ${limits?.window_hours || 24} hours`
                : "Refresh the tier limit from Meta before relying on campaign capacity."}
            </Text>
          </Flex>
          <Button variant="soft" onClick={onRefresh} loading={isRefreshing} disabled={isRefreshing}>
            Refresh limit
          </Button>
        </Flex>

        <Progress value={usedPercent} color={usedPercent >= 100 ? "red" : usedPercent >= 80 ? "amber" : "green"} />
        <Text size="1" color="gray">
          Campaigns and automation messages pause when capacity is exhausted or the limit is unknown. Usage counts sends through this app; sends through other tools are not included. Limits refresh hourly.
        </Text>
        {limits?.has_unknown_limits ? <Text size="1" color="amber">Some account limits are unknown; totals include only accounts with a numeric limit.</Text> : null}
        <WhatsappMessagingLimitHelp />

        {accounts.length ? (
          <Flex direction="column" gap="2" style={{ maxHeight: 160, overflow: "auto", paddingRight: 4 }}>
            {accounts.map((account) => {
              const accountHasLimit = account.limit !== null && typeof account.limit !== "undefined";
              return (
                <Flex key={account.id} justify="between" gap="3" align="center" style={{ borderTop: "1px solid var(--gray-a5)", paddingTop: "var(--space-2)" }}>
                  <Flex direction="column" gap="1" style={{ minWidth: 0 }}>
                    <Text size="2" weight="medium" style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {account.account_name || account.business_id}
                    </Text>
                    <Text size="1" color="gray">
                      {account.tier || "No tier"} · {account.refresh_stale ? "stale · " : ""}{account.config_count > 1 ? `${account.config_count} configs · ` : ""}refreshed {formatDateTime(account.refreshed_at)}
                    </Text>
                  </Flex>
                  <Text size="2" color={accountHasLimit && Number(account.remaining || 0) <= 0 ? "red" : "gray"} style={{ whiteSpace: "nowrap" }}>
                    {accountHasLimit
                      ? `${formatNumber(account.remaining)} / ${formatNumber(account.limit)} left`
                      : account.limit_known ? "Unlimited" : "Unknown — sending paused"}
                  </Text>
                </Flex>
              );
            })}
          </Flex>
        ) : null}
      </Flex>
    </SectionCard>
  );
}

export default function MarketingWhatsappAnalytics() {
  const { userProfile } = useProfileContext();
  const { setToastMessage } = useToast();
  const organisationId = userProfile?.organisation_id;
  const { analytics, isLoading, refetch: refetchAnalytics } = useWhatsappAnalytics(organisationId);
  const refreshMessagingLimits = useRefreshWhatsappMessagingLimits();
  const analyticsSummary = analytics.summary || {};
  const failedCount = analyticsSummary.failed;
  const dailyActivity = analytics.daily_activity || [];
  const templatePerformance = analytics.top_templates || [];
  const senderPerformance = analytics.sender_numbers || [];

  const funnelData = [
    { name: "Sent", value: analyticsSummary.sent, fill: FUNNEL_COLORS[0] },
    { name: "Delivered", value: analyticsSummary.delivered, fill: FUNNEL_COLORS[1] },
    { name: "Read", value: analyticsSummary.read, fill: FUNNEL_COLORS[2] },
  ];
  const hasFunnelData = funnelData.some((item) => item.value > 0);
  const hasDailyActivity = dailyActivity.some((day) => day.sent || day.delivered || day.read || day.failed);

  const handleRefreshMessagingLimits = async () => {
    if (!organisationId) return;
    setToastMessage("Refreshing WhatsApp messaging limits from Meta");
    try {
      await refreshMessagingLimits.mutateAsync({ organisation_id: organisationId });
      await refetchAnalytics();
      setToastMessage("WhatsApp messaging limits refreshed");
    } catch (error) {
      setToastMessage(error?.response?.data?.error || "Unable to refresh WhatsApp messaging limits");
    }
  };

  return (
    <Flex direction="column" gap="3" style={{ alignSelf: "stretch", width: "100%" }}>
      {isLoading && (
        <Progress size="1" duration="1" style={{ position: "sticky", top: 0, zIndex: 1 }} />
      )}

      <MessagingLimitCard
        limits={analytics.messaging_limits}
        onRefresh={handleRefreshMessagingLimits}
        isRefreshing={refreshMessagingLimits.isPending}
      />

      <Flex gap="2" wrap="wrap" style={{ alignSelf: "stretch" }}>
        <MetricCard
          label="Messages sent"
          value={formatNumber(analyticsSummary.sent)}
          detail={`${formatNumber(analytics.unique_recipients)} unique recipients`}
        />
        <MetricCard
          label="Delivery rate"
          value={formatPercent(analyticsSummary.delivery_rate)}
          detail={`${formatNumber(analyticsSummary.delivered)} delivered messages`}
        />
        <MetricCard
          label="Read rate"
          value={formatPercent(analyticsSummary.read_rate)}
          detail={`${formatNumber(analyticsSummary.read)} read receipts`}
        />
        <MetricCard
          label="Failure rate"
          value={formatPercent(analyticsSummary.failure_rate)}
          detail={`${formatNumber(failedCount)} failures / ${formatNumber(analyticsSummary.attempted)} attempts`}
        />
      </Flex>

      <Flex gap="3" wrap="wrap" style={{ alignSelf: "stretch" }}>
        <SectionCard
          title="Lifecycle Funnel"
          subtitle="Sent to delivered to read"
          style={{ flex: "1 1 340px", minHeight: 260 }}
        >
          {hasFunnelData ? (
            <ResponsiveContainer width="100%" height={180}>
              <FunnelChart>
                <ChartTooltip formatter={(value) => formatNumber(value)} />
                <Funnel dataKey="value" data={funnelData} isAnimationActive>
                  <LabelList position="right" fill="var(--gray-12)" stroke="none" dataKey="name" />
                </Funnel>
              </FunnelChart>
            </ResponsiveContainer>
          ) : (
            <EmptyChart label="No WhatsApp lifecycle activity yet" />
          )}
        </SectionCard>

        <SectionCard
          title="Message Momentum"
          subtitle="Fourteen-day delivery, read, and failure movement"
          style={{ flex: "2 1 520px", minHeight: 260 }}
        >
          {hasDailyActivity ? (
            <ResponsiveContainer width="100%" height={185}>
              <AreaChart data={dailyActivity}>
                <defs>
                  <linearGradient id="whatsappDeliveredGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor={EVENT_COLORS.delivered} stopOpacity={0.35} />
                    <stop offset="95%" stopColor={EVENT_COLORS.delivered} stopOpacity={0.02} />
                  </linearGradient>
                  <linearGradient id="whatsappReadGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor={EVENT_COLORS.read} stopOpacity={0.3} />
                    <stop offset="95%" stopColor={EVENT_COLORS.read} stopOpacity={0.02} />
                  </linearGradient>
                </defs>
                <CartesianGrid stroke="var(--gray-a5)" vertical={false} />
                <XAxis dataKey="day" tickLine={false} axisLine={false} />
                <YAxis allowDecimals={false} tickLine={false} axisLine={false} />
                <ChartTooltip />
                <Area type="monotone" dataKey="delivered" stroke={EVENT_COLORS.delivered} fill="url(#whatsappDeliveredGradient)" strokeWidth={2} />
                <Area type="monotone" dataKey="read" stroke={EVENT_COLORS.read} fill="url(#whatsappReadGradient)" strokeWidth={2} />
                <Area type="monotone" dataKey="failed" stroke={EVENT_COLORS.failed} fill="transparent" strokeWidth={2} />
              </AreaChart>
            </ResponsiveContainer>
          ) : (
            <EmptyChart label="No recent WhatsApp activity to chart" />
          )}
        </SectionCard>
      </Flex>

      <Flex gap="3" wrap="wrap" style={{ alignSelf: "stretch" }}>
        <SectionCard
          title="Top Templates"
          subtitle="Read rate and failure pressure by message"
          style={{ flex: "1 1 520px", minHeight: 290 }}
        >
          {templatePerformance.length ? (
            <Box style={{ maxHeight: 220, overflow: "auto", paddingRight: 4 }}>
              <Flex direction="column" gap="3">
                {templatePerformance.map((template) => (
                  <Box key={template.name}>
                    <Flex justify="between" align="center" gap="3" mb="2">
                      <Text
                        size="2"
                        weight="medium"
                        title={template.name}
                        style={{ minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                      >
                        {template.name}
                      </Text>
                      <Text size="1" color="gray" style={{ flex: "0 0 auto" }}>
                        {formatNumber(template.sent)} sent
                      </Text>
                    </Flex>
                    <Flex gap="2" align="center">
                      <Text size="1" color="gray" style={{ width: 58 }}>Read</Text>
                      <Box style={{ flex: 1, minWidth: 0 }}>
                        <Progress value={template.read_rate} color="blue" />
                      </Box>
                      <Text size="1" color="gray" style={{ width: 42, textAlign: "right" }}>{formatPercent(template.read_rate)}</Text>
                    </Flex>
                    <Flex gap="2" align="center" mt="1">
                      <Text size="1" color="gray" style={{ width: 58 }}>Failed</Text>
                      <Box style={{ flex: 1, minWidth: 0 }}>
                        <Progress value={template.failure_rate} color={template.failure_rate > 0 ? "red" : "gray"} />
                      </Box>
                      <Text size="1" color="gray" style={{ width: 42, textAlign: "right" }}>{formatPercent(template.failure_rate)}</Text>
                    </Flex>
                  </Box>
                ))}
              </Flex>
            </Box>
          ) : (
            <EmptyChart label="No template performance yet" />
          )}
        </SectionCard>

        <SectionCard
          title="Sender Numbers"
          subtitle="Which WhatsApp phone numbers are carrying volume"
          style={{ flex: "1 1 420px", minHeight: 290 }}
        >
          {senderPerformance.length ? (
            <Flex direction="column" gap="3" style={{ maxHeight: 220, overflow: "auto", paddingRight: 4 }}>
              {senderPerformance.map((sender) => (
                <Box key={sender.sender}>
                  <Flex justify="between" align="center" gap="3" mb="2">
                    <Text
                      size="2"
                      weight="medium"
                      title={sender.sender}
                      style={{ minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                    >
                      {sender.sender}
                    </Text>
                    <Text size="2" color="gray" style={{ flex: "0 0 auto" }}>{formatNumber(sender.sent)} sent</Text>
                  </Flex>
                  <Flex gap="2" align="center">
                    <Box style={{ flex: 1 }}>
                      <Progress value={sender.delivery_rate} color="green" />
                    </Box>
                    <Text size="1" color="gray" style={{ width: 82, textAlign: "right" }}>{formatPercent(sender.delivery_rate)} delivered</Text>
                  </Flex>
                  <Flex gap="2" align="center" mt="1">
                    <Box style={{ flex: 1 }}>
                      <Progress value={sender.read_rate} color="blue" />
                    </Box>
                    <Text size="1" color="gray" style={{ width: 82, textAlign: "right" }}>{formatPercent(sender.read_rate)} read</Text>
                  </Flex>
                </Box>
              ))}
            </Flex>
          ) : (
            <EmptyChart label="No sender activity yet" />
          )}
        </SectionCard>
      </Flex>

      <ToastAlert />
    </Flex>
  );
}
